/* eslint-disable prettier/prettier */
import * as React from "react";
import {
	Grid,
	TextField,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
} from "@mui/material";
import { getYodaDateToday } from "../utils/DateHelper";
import { getHeaders } from "../utils/HeaderHelper";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckboxList from "../common/CheckboxList";
import NewTextField from "../form/NewTextField";

interface Props {
  csrfToken: string;
  setSearchPatients: any;
  user_id: string;
  patientListId: string;
  setCheckedList: any;
  setOnList: any;
  pendingAdd: any;
  setPendingAdd: any;
  setPendingRemoval: any;
  checkedList: Function;
  setParamsPresent: Function;
	setLoading: Function;
  currentPage: number;
  setCurrentPage: Function;
  setTotalPages: Function;
  sortObject: any;
  setSortObject: any;
  allMatchingPatientIds: number[];
  setAllMatchingPatientIds: Function;
  isSelectAllActive: boolean;
  setIsSelectAllActive: Function;
  getAllMatchingPatients: () => any[]; // Function to get all matching patients
  allMatchingPatientsRef: React.MutableRefObject<any[]>; // Ref to store all matching patients
}

const SearchToolsWidget: React.FC<Props> = (props: any) => {
  const [programsExpanded, setProgramsExpanded] = React.useState(false);
  const [diagnosisCodesExpanded, setDiagnosisCodesExpanded] =
    React.useState(false);
  const [insuranceTypesExpanded, setInsuranceTypesExpanded] =
    React.useState(false);
  const [ltcFacilitiesExpanded, setLtcFacilitiesExpanded] =
    React.useState(false);
  const [cgmExpanded, setCGMExpanded] = React.useState(false);

  const [firstName, setFirstName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [dateOfBirth, setDateOfBirth] = React.useState<string>("");
  const [tagCount, setTagCount] = React.useState<number>(null);

  const [checkedPrograms, setCheckedPrograms] = React.useState<string[]>([]);
  const [checkedDiagnosisCodes, setCheckedDiagnosisCodes] = React.useState<
    string[]
  >([]);
  const [checkedInsuranceTypes, setCheckedInsuranceTypes] = React.useState<
    string[]
  >([]);
  const [checkedLtcFacilities, setCheckedLtcFacilities] = React.useState<
    string[]
  >([]);

  // Add state for CGM checkbox (true/false)
  const [checkedCGM, setCheckedCGM] = React.useState<boolean>(false);

  const [programs, setPrograms] = React.useState<[]>([]);
  const [diagnosisCodes, setDiagnosisCodes] = React.useState<[]>([]);
  const [insuranceTypes, setInsuranceTypes] = React.useState<[]>([]);
  const [ltcFacilities, setLtcFacilities] = React.useState<[]>([]);

  const accordionHeight = "20px";

  const accordions = [
    {
      label: "Programs",
      expanded: programsExpanded,
      setExpanded: setProgramsExpanded,
      tag: programs.length,
      items: programs,
      checkedItems: checkedPrograms,
      setCheckedItems: setCheckedPrograms,
      labelField: "title",
    },
    {
      label: "Diagnosis Codes",
      expanded: diagnosisCodesExpanded,
      setExpanded: setDiagnosisCodesExpanded,
      tag: diagnosisCodes.length,
      items: diagnosisCodes,
      checkedItems: checkedDiagnosisCodes,
      setCheckedItems: setCheckedDiagnosisCodes,
      labelField: "diagnosis_code_value",
    },
    {
      label: "Insurance Types",
      expanded: insuranceTypesExpanded,
      setExpanded: setInsuranceTypesExpanded,
      tag: insuranceTypes.length,
      items: insuranceTypes,
      checkedItems: checkedInsuranceTypes,
      setCheckedItems: setCheckedInsuranceTypes,
      labelField: "insurance_type",
    },
    {
      label: "Living Facilities",
      expanded: ltcFacilitiesExpanded,
      setExpanded: setLtcFacilitiesExpanded,
      tag: ltcFacilities.length,
      items: ltcFacilities,
      checkedItems: checkedLtcFacilities,
      setCheckedItems: setCheckedLtcFacilities,
      labelField: "name",
    },
    {
      label: "CGM",
      expanded: cgmExpanded,
      setExpanded: setCGMExpanded,
      tag: 0,
    },
  ];
  const hasMounted = React.useRef(false);
  const timeoutId: any = React.useRef(null);

  // Fetch filters
  React.useEffect(() => {
    initialFetch();
  }, []);

  const fetchAllPatientsForList = () => {
    // Fetch all patients for the list to get all saved IDs
    const params = `patient_list=${props.patientListId}&page=1&per_page=10000`;
    
    return fetch(`/data_fetching/patient_index?${params}`, {
      method: "GET",
      headers: getHeaders(props.csrfToken),
    })
      .then((result) => result.json())
      .then((data) => {
        if (data.patients && data.patients.length > 0) {
          // Set checkedList with ALL patient IDs that are on the list (not just current page)
          const allCheckedPatientIds = data.patients.filter((p) => p.on_list).map((p) => p.id);
          props.setCheckedList(allCheckedPatientIds);
          props.setOnList(allCheckedPatientIds);
          // Store all matching patient IDs and objects
          props.setAllMatchingPatientIds(data.patients.map((p) => p.id));
          props.allMatchingPatientsRef.current = data.patients || [];
          // Return the data so it can be used to populate searchPatients
          return data;
        }
        return null;
      })
      .catch((error) => {
        console.log("Error fetching all patients for list:", error);
        return null;
      });
  };

  const initialFetch = () => {
    props.setCurrentPage(1);

    if (props.patientListId){
      // In edit mode, first fetch all patients to populate checkedList
      // Then fetch first page for display
      fetchAllPatientsForList().then((allPatientsData) => {
        // After fetching all patients, fetch filters and first page
        const params = `patient_list=${props.patientListId}&page=1&per_page=10`;
        
        fetch(`/data_fetching/patient_index?${params}`, {
          method: "GET",
          headers: getHeaders(props.csrfToken),
        })
          .then((result) => result.json())
          .then((data) => {
            props.setLoading(false);
            setPrograms(data.programs);
            setDiagnosisCodes(data.diagnosis_codes);
            setInsuranceTypes(data.insurance_types);
            setLtcFacilities(data.ltc_facilities);
            setTagCount(data.tag_count);
            
            // Use patients from the paginated fetch if available, otherwise use allPatientsData
            if (data.patients && data.patients.length > 0) {
              props.setSearchPatients(data.patients);
              props.setAllMatchingPatientIds(data.patients.map((p) => p.id));
              if (data.total_pages !== undefined) {
                props.setTotalPages(data.total_pages);
              } else {
                // Calculate total pages from allPatientsData if available
                const totalPages = allPatientsData && allPatientsData.patients 
                  ? Math.ceil(allPatientsData.patients.length / 10)
                  : 1;
                props.setTotalPages(totalPages);
              }
            } else if (allPatientsData && allPatientsData.patients && allPatientsData.patients.length > 0) {
              // If paginated fetch returned empty but we have all patients, use first page from allPatientsData
              const firstPagePatients = allPatientsData.patients.slice(0, 10);
              props.setSearchPatients(firstPagePatients);
              props.setTotalPages(Math.ceil(allPatientsData.patients.length / 10));
            } else {
              // No patients found at all
              props.setSearchPatients([]);
              props.setTotalPages(1);
            }
          })
          .catch((error) => {
            props.setLoading(false);
            console.log(error);
            // On error, try to use allPatientsData if available
            if (allPatientsData && allPatientsData.patients && allPatientsData.patients.length > 0) {
              const firstPagePatients = allPatientsData.patients.slice(0, 10);
              props.setSearchPatients(firstPagePatients);
              props.setTotalPages(Math.ceil(allPatientsData.patients.length / 10));
            }
          });
      }).catch((error) => {
        props.setLoading(false);
        console.log("Error in fetchAllPatientsForList:", error);
        // Fallback: try regular fetch even if fetchAllPatientsForList failed
        const params = `patient_list=${props.patientListId}&page=1&per_page=10`;
        fetch(`/data_fetching/patient_index?${params}`, {
          method: "GET",
          headers: getHeaders(props.csrfToken),
        })
          .then((result) => result.json())
          .then((data) => {
            props.setLoading(false);
            setPrograms(data.programs);
            setDiagnosisCodes(data.diagnosis_codes);
            setInsuranceTypes(data.insurance_types);
            setLtcFacilities(data.ltc_facilities);
            setTagCount(data.tag_count);
            if (data.patients && data.patients.length > 0) {
              props.setSearchPatients(data.patients);
              props.setTotalPages(data.total_pages || 1);
            } else {
              props.setSearchPatients([]);
              props.setTotalPages(1);
            }
          });
      });
    }
    else {
      // Create mode - fetch filters only
      const params = `filters_only=true&page=1`;
      fetch(`/data_fetching/patient_index?${params}`, {
        method: "GET",
        headers: getHeaders(props.csrfToken),
      })
        .then((result) => result.json())
        .then((data) => {
          props.setLoading(false);
          setPrograms(data.programs);
          setDiagnosisCodes(data.diagnosis_codes);
          setInsuranceTypes(data.insurance_types);
          setLtcFacilities(data.ltc_facilities);
          setTagCount(data.tag_count);
          
          if (data.patients && data.patients.length > 0) {
            props.setSearchPatients(data.patients);
            const checkedPatientIds = data.patients.filter((p) => p.on_list).map((p) => p.id);
            props.setCheckedList(checkedPatientIds);
            props.setOnList(checkedPatientIds);
            props.setAllMatchingPatientIds(data.patients.map((p) => p.id));
          }
          props.setTotalPages(data.total_pages || 1);
        })
        .catch((error) => {
          props.setLoading(false);
          console.log(error);
        });
    }
  }

  const afterClearFetch = () => {
    props.setCurrentPage(1);
    var params = '';

    if (props.patientListId){
      params = `patient_list=${props.patientListId}&page=1&per_page=10`;
    }
    else {
      params = `filters_only=true&page=1`;
    }
		// props.setLoading(true);

    fetch(`/data_fetching/patient_index?${params}`, {
      method: "GET",
      headers: getHeaders(props.csrfToken),
    })
      .then((result) => result.json())
      .then((data) => {
				props.setLoading(false);
        if (data.patients && data.patients.length > 0){
          if (props.patientListId) {
            // Update list_status when in edit mode
            const onListAndpendingAdds = [...data.patients, ...props.pendingAdd];
            props.setSearchPatients(
              onListAndpendingAdds.map((patient) => {
                const isChecked = props.checkedList.includes(patient.id);
                if (patient.list_status == "Not on List" && isChecked) {
                  return { ...patient, list_status: "Pending Add" };
                } else if (patient.list_status == "On List" && !isChecked) {
                  return { ...patient, list_status: "Pending Removal" };
                } else {
                  return patient; // No changes
                }
              })
            );
          } else {
            props.setSearchPatients(data.patients);
          }
        } else if (props.patientListId) {
          // In edit mode, if fetch returns empty, try fetchAllPatientsForList as fallback
          fetchAllPatientsForList().then((allPatientsData) => {
            if (allPatientsData && allPatientsData.patients && allPatientsData.patients.length > 0) {
              const firstPagePatients = allPatientsData.patients.slice(0, 10);
              props.setSearchPatients(firstPagePatients);
              props.setTotalPages(Math.ceil(allPatientsData.patients.length / 10));
            }
          });
        } else {
          // Show pendingAdds if create mode 
          props.setSearchPatients(props.pendingAdd.map((p) => { 
            return {...p, list_status: 'Pending Add'}
          }));
        }
        props.setAllMatchingPatientIds(data.patients && data.patients.length > 0 ? data.patients.map((p) => p.id) : []);
        props.allMatchingPatientsRef.current = data.patients && data.patients.length > 0 ? data.patients : [];
        if (data.total_pages !== undefined) {
          props.setTotalPages(data.total_pages);
        }
      })
      .catch((error) => {
				props.setLoading(false);
        console.log(error);
      });
  }

  const shouldFetchPatients = () => {
    return (
      (firstName && firstName.length >= 2) ||
      (lastName && lastName.length >= 2) ||
      dateOfBirth ||
      checkedPrograms.length > 0 ||
      checkedDiagnosisCodes.length > 0 ||
      checkedInsuranceTypes.length > 0 ||
      checkedLtcFacilities.length > 0 ||
      checkedCGM
    );
  };

  const clearAllFilters = () => {
    setFirstName("");
    setLastName("");
    setDateOfBirth("0000-00-00"); //Needed to clear DOB partial values
    setTimeout(() => setDateOfBirth(""), 0); //Need to clear DOB partial values
    setCheckedPrograms([]);
    setCheckedDiagnosisCodes([]);
    setCheckedInsuranceTypes([]);
    setCheckedLtcFacilities([]);
    setCheckedCGM(false);
  };
  
  const buildSearchParams = () => {
    const params = new URLSearchParams();
    params.append("sort_by", props.sortObject.field);
    params.append("sort_direction", props.sortObject.direction);

    if (firstName)
      params.append("first_name", firstName);
    if (lastName) 
      params.append("last_name", lastName);
    if (dateOfBirth) params.append("date_of_birth", dateOfBirth);

    if (checkedPrograms.length > 0) {
      checkedPrograms.forEach((programId) =>
        params.append("programs[]", programId)
      );
    }

    if (checkedDiagnosisCodes.length > 0) {
      checkedDiagnosisCodes.forEach((codeId) =>
        params.append("diagnosis_codes[]", codeId)
      );
    }

    if (checkedInsuranceTypes.length > 0) {
      checkedInsuranceTypes.forEach((insuranceId) =>
        params.append("insurance_types[]", insuranceId)
      );
    }

    if (checkedLtcFacilities.length > 0) {
      checkedLtcFacilities.forEach((facilityId) =>
        params.append("ltc_facilities[]", facilityId)
      );
    }

    // Add CGM to the query parameters
    params.append("is_cgm", checkedCGM.toString());

    if (props.patientListId) { //edit mode
      params.append("ref_patient_list", props.patientListId);
    }

    return params;
  };

  const getAllMatchingPatientIds = () => {
    const params = buildSearchParams();
    
    if (!params.toString()) {
      props.setAllMatchingPatientIds([]);
      return;
    }

    // Fetch all IDs by requesting a very large page size
    params.append("page", '1');
    params.append("per_page", '10000'); // Large number to get all IDs

    fetch(`/data_fetching/patient_index?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(props.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.error) {
          console.log("Error fetching all patient IDs:", result.error);
          props.setAllMatchingPatientIds([]);
        } else {
          const allIds = result.patients.map((patient) => patient.id);
          props.setAllMatchingPatientIds(allIds);
          // Store all matching patients for selectAll functionality
          props.allMatchingPatientsRef.current = result.patients || [];
        }
      })
      .catch((error) => {
        console.log("Fetch error for all patient IDs:", error);
        props.setAllMatchingPatientIds([]);
      });
  };

  const getPatients = (resetPages=false) => {
    props.setLoading(true);
    const params = buildSearchParams();
    
    if (!params.toString()) {
      return // stop if params is empty
    }

    if(resetPages) {
      params.append("page", '1');
      props.setCurrentPage(1)
    } else {
      params.append("page", props.currentPage);
    }

    fetch(`/data_fetching/patient_index?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(props.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
				props.setLoading(false);

        if (result.error) {
          console.log("Error fetching patients:", result.error);
        } else {
          // Update list_status of new data 
          // (TODO can we DRY this, it is used in afterClearFetch)
          props.setSearchPatients(
            result.patients.map((patient) => {
              const isChecked = props.checkedList.includes(patient.id);
              if (patient.list_status == "Not on List" && isChecked) {
                return { ...patient, list_status: "Pending Add" };
              } else if (patient.list_status == "On List" && !isChecked) {
                return { ...patient, list_status: "Pending Removal" };
              } else {
                return patient; // No changes
              }
            })
          );

          props.setTotalPages(result.total_pages)
        }
      })
      .catch((error) => {
				props.setLoading(false);

        console.log("Fetch error:", error);
      });
  };

  // Search and sort requests
  React.useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      if (shouldFetchPatients()) {
        getPatients(true);
        getAllMatchingPatientIds(); // Fetch all matching IDs when searching
      } else if (hasMounted.current) {
        afterClearFetch()
      }
      props.setParamsPresent(shouldFetchPatients());
      hasMounted.current = true;
    }, 250);
  }, [
    firstName,
    lastName,
    dateOfBirth,
    checkedPrograms,
    checkedDiagnosisCodes,
    checkedInsuranceTypes,
    checkedLtcFacilities,
    checkedCGM,
    props.sortObject,
  ]);

  // Pagination only
  React.useEffect(() => {
    if (!hasMounted.current) {
      return 
    }
    
    // For edit mode without filters, fetch the correct page
    if (props.patientListId && !shouldFetchPatients()) {
      props.setLoading(true);
      const params = `patient_list=${props.patientListId}&page=${props.currentPage}`;
      
      fetch(`/data_fetching/patient_index?${params}`, {
        method: "GET",
        headers: getHeaders(props.csrfToken),
      })
        .then((result) => result.json())
        .then((data) => {
          props.setLoading(false);
          if (data.patients.length > 0) {
            // Update list_status based on checkedList (which has all saved IDs)
            const onListAndpendingAdds = [...data.patients, ...props.pendingAdd];
            props.setSearchPatients(
              onListAndpendingAdds.map((patient) => {
                const isChecked = props.checkedList.includes(patient.id);
                if (patient.list_status == "Not on List" && isChecked) {
                  return { ...patient, list_status: "Pending Add" };
                } else if (patient.list_status == "On List" && !isChecked) {
                  return { ...patient, list_status: "Pending Removal" };
                } else {
                  return patient; // No changes
                }
              })
            );
          }
          props.setTotalPages(data.total_pages || 1);
        })
        .catch((error) => {
          props.setLoading(false);
          console.log(error);
        });
    } else if (shouldFetchPatients()) {
      // Has search filters, use normal getPatients
      getPatients();
    }
  }, [
    props.currentPage
  ]);

  return (
    <>
      <Grid item xs={12}>
        <Grid container>
          <Grid
            container
            justifyContent="space-between"
            className="widget-container"
          >
            <Grid item xs={12} className="widget-header">
              <h3>Search Tools</h3>
            </Grid>
          </Grid>
          <div className="divider"></div>
          <Grid container>
            <Grid item xs={12} className="widget-body">
              <div className="filter-container">
                <div className="filter-header">
                  <div className="header-wrapper">
                    <div className="header">Patient Filters</div>
                    {shouldFetchPatients() && 
                      <button onClick={clearAllFilters} className="clear-all">
                        <svg
                          width="9"
                          height="9"
                          viewBox="0 0 8 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          >
                          <rect
                            y="7.99902"
                            width="8"
                            height="8"
                            rx="1"
                            transform="rotate(-90 0 7.99902)"
                            fill="#FF890A"
                          />
                          <path
                            d="M2.13699 1.66585L1.66699 2.13585L3.53033 3.99919L1.66699 5.86252L2.13699 6.33252L4.00033 4.46919L5.86366 6.33252L6.33366 5.86252L4.47033 3.99919L6.33366 2.13585L5.86366 1.66585L4.00033 3.52919L2.13699 1.66585Z"
                            fill="white"
                          />
                        </svg>
                        <span>Clear All</span>
                      </button>
                  }
                  </div>
                  <div className="tag-number">
                    {tagCount > 0 ? tagCount : 0} existing patient tags
                  </div>
                </div>

                <div className="filter-body">
                  <Grid item xs={12} className="field-container">
                  <NewTextField
                    id="first_name"
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                  />
                  </Grid>
                  <Grid item xs={12} className="field-container">
                  <NewTextField
                    id="last_name"
                    label="Last Name"
                    value={lastName} 
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="First Name"
                  />
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="date"
                      label="Date of birth"
                      value={dateOfBirth}
                      className="textInput plainLabel small-label"
                      type="date"
                      variant="filled"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ disableUnderline: true }}
                      inputProps={{ max: `${getYodaDateToday()}` }}
                      onChange={(event) => setDateOfBirth(event.target.value)}
                    />
                  </Grid>
                </div>
                <div className="tool-list">
                  {accordions.map(
                    (
                      {
                        label,
                        expanded,
                        setExpanded,
                        tag,
                        items,
                        checkedItems,
                        setCheckedItems,
                        labelField,
                      },
                      index
                    ) => (
                      <Accordion
                        key={index}
                        expanded={expanded}
                        onChange={() => setExpanded(!expanded)}
                        sx={{
                          boxShadow: "none",
                          border: "none",
                          "&:before": { display: "none" },
                          "&. Mui-expanded": { margin: 0 },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={null}
                          sx={{
                            padding: 0,
                            minHeight: accordionHeight,
                            "&.MuiAccordionSummary-root": {
                              minHeight: accordionHeight,
                            },
                            "&.MuiAccordionSummary-gutters": {
                              padding: 0,
                              minHeight: accordionHeight,
                            },
                            "&.Mui-expanded": { minHeight: accordionHeight },
                            "& .MuiAccordionSummary-content": {
                              margin: 0,
                              alignItems: "center",
                              minHeight: accordionHeight,
                            },
                            "& .MuiAccordionSummary-contentGutters": {
                              marginBottom: "10px",
                              minHeight: accordionHeight,
                            },
                            "& .MuiAccordionSummary-content.Mui-expanded": {
                              margin: 0,
                              minHeight: accordionHeight,
                              alignItems: "center",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              p: "0 20px 0 12px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                transform: expanded
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s ease-in-out",
                              }}
                            >
                              <ArrowDropDownIcon />
                            </Box>
                            <p className="collapse-list-header">{label}</p>
                            {label !== "CGM" ? (
                              <div className="tool-badge">{tag}</div>
                            ) : (
                              <div></div>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: 0,
                            backgroundColor: "#F8F8F8",
                            p: "20px 20px 12px 16px",
                          }}
                        >
                          {label === "CGM" ? (
                            <div className="multiple-checkbox">
                              <div className="checkbox-row-container">
                                <label className="option-text">
                                  <input
                                    type="checkbox"
                                    onChange={(e) =>
                                      setCheckedCGM(e.target.checked)
                                    }
                                    checked={checkedCGM}
                                    className="checkbox"
                                  />
                                  CGM Enabled
                                </label>
                              </div>
                            </div>
                          ) : (
                            <CheckboxList
                              items={items}
                              checkedItems={checkedItems}
                              setCheckedItems={setCheckedItems}
                              labelField={labelField}
                            />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SearchToolsWidget;
